import { useCallback, useEffect, useMemo, useState } from 'react';

import { generateFilePreview } from '@atlassian/conversation-assistant-chat-prompt-input/generateFilePreview';
import { isTextFile } from '@atlassian/conversation-assistant-chat-prompt-input/utils/fileTypeHelpers';
import { type MessageFileId } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { fileCache } from '../utils/file-cache';

/**
 * Custom hook to manage file caching and preview generation for post-send thumbnails.
 *
 * **How it works:**
 * - Returns `cacheFiles` function to cache File objects by ID (call from onSubmit callbacks)
 * - Returns `blobUrls` record with generated preview blob URLs for each file
 * - Returns `isLoading` record indicating loading state for each file
 * - Returns `fileObjects` record with cached File objects for modal preview
 *
 * **Usage:**
 * - Call `cacheFiles(files)` from chat-input-refresh on message send (extracts fileObject from UploadedFile[])
 * - FileCard uses `blobUrls` to display thumbnails for images, PDFs, and text files
 * - FileCard uses `fileObjects` to open full preview modal on click
 *
 * **Current Limitation**: Only works for files uploaded in the current session (backend doesn't return preview URLs).
 *
 * **IMPORTANT**: Only invoke this hook from components that are actively rendering the files.
 * Blob URLs are created eagerly when files change, so calling this hook prematurely
 * (e.g., in parent components that don't render images) will create unnecessary memory overhead.
 *
 * @param files - Array of files with IDs
 * @returns Object with cacheFiles function, blobUrls record, isLoading record, and fileObjects record
 */
export function useFileThumbnails(files: MessageFileId[]): {
	cacheFiles: (filesToCache: Array<{ id: string; fileObject: File }>) => void;
	blobUrls: Record<string, string>;
	isLoading: Record<string, boolean>;
	fileObjects: Record<string, File>;
} {
	const [blobUrls, setBlobUrls] = useState<Record<string, string>>({});
	const [previewErrors, setPreviewErrors] = useState<Record<string, string>>({});

	const cacheFiles = useCallback((filesToCache: Array<{ id: string; fileObject: File }>) => {
		filesToCache.forEach((file) => {
			if (
				file.fileObject.type.startsWith('image/') ||
				file.fileObject.type === 'application/pdf' ||
				isTextFile(file.fileObject.type, file.fileObject.name)
			) {
				fileCache.set(file.id, file.fileObject);
			}
		});
	}, []);

	const fileObjects = useMemo(() => {
		const cachedFileObjects: Record<string, File> = {};
		files.forEach((file) => {
			if (file.id) {
				const sourceFile = fileCache.get(file.id);
				if (sourceFile) {
					cachedFileObjects[file.id] = sourceFile;
				}
			}
		});
		return cachedFileObjects;
	}, [files]);

	const isLoading = useMemo(() => {
		const loading: Record<string, boolean> = {};
		Object.keys(fileObjects).forEach((id) => {
			loading[id] = !blobUrls[id] && !previewErrors[id];
		});
		return loading;
	}, [fileObjects, blobUrls, previewErrors]);
	useEffect(() => {
		const fileIds = Object.keys(fileObjects);
		if (fileIds.length === 0) {
			return;
		}

		Promise.allSettled(
			fileIds.map(async (id) => {
				const url = await generateFilePreview(fileObjects[id]);
				return { id, url };
			}),
		).then((results) => {
			const urls: Record<string, string> = {};
			const errors: Record<string, string> = {};

			results.forEach((result, index) => {
				if (result.status === 'fulfilled') {
					urls[result.value.id] = result.value.url;
				} else {
					const error = result.reason;
					errors[fileIds[index]] = error instanceof Error ? error.message : String(error);
				}
			});

			if (Object.keys(urls).length > 0) {
				setBlobUrls(urls);
			}
			if (Object.keys(errors).length > 0) {
				setPreviewErrors(errors);
			}
		});
	}, [fileObjects]);

	useEffect(() => {
		return () => {
			Object.values(blobUrls).forEach((url) => {
				if (url) {
					URL.revokeObjectURL(url);
				}
			});
		};
	}, [blobUrls]);

	return { cacheFiles, blobUrls, isLoading, fileObjects };
}
